import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { AutocompleteProps } from '@mui/material';

import { TextField, TextFieldProps } from 'TextField';
import { IconName, Icons } from 'Icon';

import { TimePickerAutocomplete } from './TimePicker.styled';
import {
  createTimeOptions,
  getDateFromTime,
  getNearestTimeOption,
  parseDateFromString,
} from './timePickerHandlers';

const TIME_REG_EXP = /^\d{1,2}:?\d{0,2} ?([ap]m?)?$/i;

type TimePickerProps = Omit<
  AutocompleteProps<TextFieldProps, false, false, true>,
  'renderInput' | 'options' | 'value'
> & {
  icon?: IconName;
  label?: string;
  helperText?: string;
  hiddenLabel?: boolean;
  step?: number;
  is24Hours?: boolean;
  error?: boolean;
  onChange?: (date: Date) => void;
  value?: Date;
  id: string;
};

const HEIGHT_FOR_OPTIONS_CONFIG = {
  sx: {
    maxHeight: '216px',
    'li.MuiAutocomplete-option': {
      minHeight: '40px',
    },
  },
};

function TimePicker({
  error,
  helperText,
  hiddenLabel = false,
  step = 15,
  is24Hours = false,
  value = getDateFromTime(9, 0),
  label,
  icon = Icons.time,
  id,
  onChange = () => {},
  ...props
}: TimePickerProps) {
  const formatValue = useCallback(
    (value: Date) => {
      return format(value, is24Hours ? 'HH:mm' : 'p').toLowerCase();
    },
    [is24Hours],
  );
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatValue(value));
  const previousSelectedDate = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeOptions = useMemo(() => createTimeOptions(step, is24Hours), [step, is24Hours]);
  const listboxIntervalRef = useRef<ReturnType<typeof setInterval>>();

  function onInputChange(_: unknown, newValue = '') {
    if (!newValue || TIME_REG_EXP.test(newValue)) {
      setInputValue(newValue);
    }
  }

  const saveInputValue = useCallback(
    (timeValue) => {
      if (!timeValue) {
        setInputValue('');
      } else {
        const updatedDate = parseDateFromString(timeValue);
        const newValue = formatValue(updatedDate);
        setInputValue(newValue);
      }
    },
    [formatValue],
  );

  function onChangeHandler(_: unknown, timeValue: string) {
    if (timeValue) {
      const updatedDate = parseDateFromString(timeValue);
      onChange(updatedDate);
    }
  }

  function onInputBlur(_: unknown, timeValue: string) {
    if (timeValue) {
      onChangeHandler(_, timeValue);
      if (!inputRef.current?.value) {
        saveInputValue(formatValue(value));
      }
    }
  }

  const onOpen = useCallback(() => {
    if (open) {
      return false;
    }

    setOpen(true);

    if (value && id) {
      listboxIntervalRef.current = setInterval(() => {
        const listbox = document.querySelector<HTMLUListElement>(`#${id}-listbox`);
        if (listbox) {
          const nearestOption = getNearestTimeOption(value, timeOptions, is24Hours);
          const listboxOption = Array.from(listbox.children).find((o) => o.textContent === nearestOption);
          const { top: parentTop } = listbox.getBoundingClientRect();
          const { top: optionTop = 0 } = listboxOption?.getBoundingClientRect() || {};
          listbox.scrollTop = optionTop - parentTop + listbox.scrollTop;
          clearInterval(listboxIntervalRef.current!);
        }
      }, 0);
    }
  }, [value, id, timeOptions, is24Hours, open]);

  function onClose() {
    setOpen(false);
    listboxIntervalRef.current = undefined;
  }

  useEffect(() => {
    if (value > previousSelectedDate.current || value < previousSelectedDate.current) {
      saveInputValue(formatValue(value));
    }
  }, [value, formatValue, saveInputValue]);

  useEffect(() => {
    function onIconClick() {
      inputRef.current?.focus();
      onOpen();
    }

    const container = inputRef.current?.parentNode;
    const icon = container?.querySelector('.MuiInputAdornment-root');

    icon?.addEventListener('click', onIconClick);

    return () => {
      icon?.removeEventListener('click', onIconClick);
    };
  }, [onOpen]);

  return (
    <TimePickerAutocomplete
      filterOptions={(option) => option}
      freeSolo
      id={id}
      inputValue={inputValue}
      isOptionEqualToValue={() => false}
      onBlur={() => onInputBlur(null, inputValue || formatValue(value))}
      onChange={onChangeHandler}
      onClose={onClose}
      onInputChange={onInputChange}
      onOpen={onOpen}
      open={open}
      {...props}
      ListboxProps={{
        ...HEIGHT_FOR_OPTIONS_CONFIG,
        ...props.ListboxProps,
      }}
      options={timeOptions}
      renderInput={(params) => {
        return (
          <TextField
            error={error}
            helperText={helperText}
            hiddenLabel={hiddenLabel}
            icon={icon}
            inputRef={inputRef}
            label={label}
            {...params}
          />
        );
      }}
    />
  );
}

export default TimePicker;
export { TimePicker };
export type { TimePickerProps };
